import { BEIGE_600, GRAY_900 } from 'constants/styling/theme'
import { FC, ReactNode } from 'react'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { CircleIcon } from 'components/common/CircleIcon'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/**
 * @interface Props
 */
interface Props {
  /** Title of empty content info box */
  title: string
  /** Description of empty content info box */
  description?: string
  /** Icon for empty content info box */
  icon?: ReactNode
  /** Action primary button of empty content info box */
  button?: ReactNode
  /** Heading element of the title, to fit the page outline; without it the title is not a heading */
  titleComponent?: 'h1' | 'h2' | 'h3' | 'h4'
  /** Additional class on the root box */
  className?: string
}

/**
 * @component
 * EmptyContent component displays a placeholder screen when there is no content to show.
 *
 * @example
 * <EmptyContent
 *   title="No Messages"
 *   description="Michael put them into the special filing cabinet"
 *   icon={<MessageIcon />}
 *   button={<Button onClick={handleReload}>Take them out of the bin</Button>}
 * />
 */
export const EmptyContent: FC<Props> = ({
  icon,
  title,
  description,
  button,
  titleComponent,
  className,
}) => {

  return (
    <BorderBoxWrapper
      className={className}
      backgroundColor={BEIGE_600}
      gap={2}
      padding={4}
      display="flex"
      flexDirection="column"
      flexWrap="nowrap"
      alignItems="center"
      justifyContent="center"
    >

      {/** CIRCLE WITH ICON */}
      {icon &&
        <CircleIcon
          icon={icon}
        />
      }

      {/** INFO */}
      <Stack direction="column" alignItems="center" textAlign="center">

        {!titleComponent &&
          <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
            {title}
          </Typography>
        }

        {!!titleComponent &&
          <Typography
            variant="text-lg"
            component={titleComponent}
            fontWeight={600}
            color={GRAY_900}
            // `&&` outranks the old-style routes' global h1-h4 sizes and colour (src/styles/index.sass)
            sx={theme => ({ '&&': { fontSize: theme.typography['text-lg']?.fontSize, lineHeight: theme.typography['text-lg']?.lineHeight, color: GRAY_900 } })}
          >
            {title}
          </Typography>
        }

        {!!description && <Typography variant="text-sm" marginTop={1.2}>{description}</Typography>}

        {!!button && button}

      </Stack>

    </BorderBoxWrapper>
  )
}
